import {
  ExperienceTypeEnum,
  ExtraRequirementEnum,
  ExtraScopeEnum,
} from '@koob/enums';
import { distanceInKm } from './geo';

/**
 * KD-3548 — where a day actually ends, once a required transfer is taken into
 * account.
 *
 * A program declares its own end location, but when a required transfer carries
 * the traveller to the next one, the day does not end where the program says: a
 * Hanoi -> Hue transfer means the day ends in Hue, and the hotel search, the
 * conflict badge and the day label all have to follow. An optional transfer
 * changes nothing.
 *
 * Derived, never stored. Recomputed on every read, which is what makes the rule
 * retroactive on itineraries saved before this ticket and self-healing when the
 * transfer is replaced, removed, or when programs are reordered — there is no
 * cached location left to invalidate, so none can be inherited.
 *
 * The two apps hold their selected extras differently (a flat context array in
 * dmc, a per-day zustand field in to), so the extras of the day enter already
 * filtered by the caller: that is the only signature both architectures can
 * share.
 */

const asArray = (value) => (Array.isArray(value) ? value : []);

// Everywhere an extra can be described. The "required" flag qualifies the
// parent -> child edge, so a required transfer is usually not listed among the
// experience's own extras: it hangs off one of them. Both scopes are catalogued
// the same way — { id, experienceType, required[], suggested[] } — global extras
// on the experience, day extras on the program (Program.suggestedExperiences).
// The next experience is searched too: on the day two programs meet, the
// transfer can just as well be configured on the one being reached.
const getCatalogEntries = ({ program, currentExperience, nextExperience }) => [
  ...asArray(currentExperience?.extras),
  ...asArray(program?.suggestedExperiences),
  ...asArray(nextExperience?.extras),
];

const findCatalogEntry = (extraId, entries) => {
  const direct = entries.find((entry) => entry?.id === extraId);
  if (direct) {
    return direct;
  }

  // Not a top-level extra: look for it among the children of one, where it also
  // carries its own typology.
  for (const parent of entries) {
    const child =
      asArray(parent?.required).find((r) => r?.id === extraId) ??
      asArray(parent?.suggested).find((s) => s?.id === extraId);

    if (child) {
      return child;
    }
  }

  return undefined;
};

/**
 * Whether a catalogue entry offers this extra as the way to reach a given
 * experience. That recommendation is what names the edge between two programs,
 * and it is the only trace of it left once the trip has been saved.
 */
const recommendsExperience = (catalogEntry, experienceId) =>
  Boolean(experienceId) &&
  asArray(catalogEntry?.recommendations).some((r) => r?.id === experienceId);

export const isTransferTypology = (extra, entries) => {
  const catalogEntry = findCatalogEntry(extra?.id, entries);

  if (catalogEntry?.experienceType) {
    return catalogEntry.experienceType === ExperienceTypeEnum.TRANSFER;
  }

  // Nothing in the catalogue to read the typology from. `selectedExtras` is
  // built by spreading the experience DETAIL payload, which carries no
  // experienceType at all, but only populates its `transfer` sub-object for an
  // experience that is one — the only typology signal left.
  return Boolean(extra?.transfer);
};

/**
 * Whether a retained extra is a required transfer.
 *
 * The flag qualifies the edge, never the node: an extra is not "required" in
 * itself, a parent extra declares it as one of its mandatory services. So we
 * never read `extra.required` — we ask the catalogue whether anything requires
 * this extra. Same pattern as UpdateTripBookingButton, already in production.
 *
 * Two ways in, because the first one does not survive a save: `type` is set when
 * the extra is picked, but TripDayOptions stores only the day, the parent and
 * the option, and the dmc rehydration whitelist drops it too. Re-deriving the
 * flag from the catalogue is therefore what does the work on reload, and what
 * makes itineraries saved before KD-3548 switch locations without being touched.
 */
const isRequiredTransfer = ({
  extra,
  program,
  currentExperience,
  nextExperience,
}) => {
  if (!extra?.id) {
    return false;
  }

  const entries = getCatalogEntries({
    program,
    currentExperience,
    nextExperience,
  });

  if (!isTransferTypology(extra, entries)) {
    return false;
  }

  // Recommended transfer connecting two programs/experiences (transitional transfer)
  if (
    extra.transitionalExperienceId ||
    extra.sourceExperienceId ||
    extra.isRecommended
  ) {
    return true;
  }

  // Set during the session the extra is picked in, and authoritative then — but
  // only when it really is the requirement level. `type` is an overloaded field:
  // the composer writes 'required' / 'suggested' into it when the extra is
  // picked, while an experience payload carries its own product type there
  // ('experience', 'transfer', 'hotel'). A reloaded extra is built by spreading
  // the experience DETAIL, so it arrives with the API's value — truthy, never
  // 'required', and enough to answer "not a required transfer" before the
  // catalogue is ever consulted. Reading it blind is what kept every reloaded
  // transfer out, whatever else was derived further down.
  if (Object.values(ExtraRequirementEnum).includes(extra.type)) {
    return extra.type === ExtraRequirementEnum.REQUIRED;
  }

  // Nothing left to read on the extra itself: it comes from a saved trip. The
  // TripDayOption record keeps only extraId / parentExtraId / adultCount /
  // childrenBirthdates, and the rehydration rebuilds the extra by spreading the
  // experience DETAIL — so every marker set when the transfer was picked
  // (`isRecommended`, `sourceExperienceId`, `transitionalExperienceId`, `type`)
  // is gone by the time the trip is reopened or exported.
  //
  // The catalogue still knows, though: a transfer that connects two programs is
  // declared by one of them and RECOMMENDS the other. Reading the recommendation
  // back is what makes the location switch survive a save — without it the day
  // silently returns to the program's own end location the moment the itinerary
  // is reloaded, which is exactly what the PDF export renders.
  // Either direction of the edge: the transfer can just as well be declared by
  // the experience being reached, pointing back at the one being left.
  //
  // The catalogue entry keeps its own requirement level in `type` — the modal
  // reads it the same way to tell a mandatory child from an optional one — and
  // it is the only place left to read it once the extra has been saved. An
  // optional transfer must never move the day (KD-3548, out-of-scope cases), so
  // an entry that declares itself suggested is turned down here rather than
  // becoming required again the moment the itinerary is reopened.
  const catalogEntry = findCatalogEntry(extra.id, entries);
  if (
    catalogEntry?.type !== ExtraRequirementEnum.SUGGESTED &&
    (recommendsExperience(catalogEntry, nextExperience?.id) ||
      recommendsExperience(catalogEntry, currentExperience?.id))
  ) {
    return true;
  }

  return entries.some((parent) =>
    asArray(parent?.required).some((r) => r?.id === extra.id),
  );
};

/**
 * Haversine distance, in kilometres, between two positions — geo.js owns the
 * formula and the tolerant coordinate read (lat/latitude, lon/lng/longitude),
 * so the alias chain cannot drift between the two conflict-detection paths.
 *
 * Returns Infinity rather than null when either position is not geolocated,
 * so a missing coordinate simply never matches a proximity test.
 */
const getDistanceKm = (pos1, pos2) => distanceInKm(pos1, pos2) ?? Infinity;

/**
 * Whether two positions designate the same place.
 *
 * Three signals, from the most reliable to the loosest: a shared identifier, a
 * shared name, then geographic proximity. The last one is what makes the chain
 * hold together across sources — a transfer arriving at "Bangkok" and the next
 * one leaving from "Bangkok Suvarnabhumi Airport" share neither id nor name, but
 * they are the same leg of the journey.
 */
const isSameLocation = (pos1, pos2, maxDistanceKm = 50) => {
  if (!pos1 || !pos2) return false;

  // 1. A shared identifier.
  const id1 = pos1.cityId ?? pos1.id ?? pos1.locationId;
  const id2 = pos2.cityId ?? pos2.id ?? pos2.locationId;
  if (id1 && id2 && id1 === id2) return true;

  // 2. A shared name.
  const name1 = (pos1.name || pos1.city || '').trim().toLowerCase();
  const name2 = (pos2.name || pos2.city || '').trim().toLowerCase();
  if (name1 && name2 && name1 === name2) return true;

  // 3. Geographic proximity.
  const dist = getDistanceKm(pos1, pos2);
  return dist <= maxDistanceKm;
};

const getTransferStart = (transfer) =>
  transfer?.startPosition ||
  transfer?.startLocation ||
  transfer?.transfer?.startPosition ||
  transfer?.transfer?.startLocation;

const getTransferEnd = (transfer) =>
  transfer?.endPosition ||
  transfer?.endLocation ||
  transfer?.transfer?.endPosition ||
  transfer?.transfer?.endLocation;

/**
 * How far a transfer leaves from a given place.
 *
 * A start that IS that place counts as zero even when nothing is geolocated:
 * positions are matched on their id and their name before their coordinates,
 * which is what keeps the chain together on the payloads that carry a cityId but
 * no lat/lon at all. Anything else is a plain distance, and an unmeasurable one
 * is Infinity — it never wins against a measurable one, but it still gets
 * chained once nothing closer is left.
 */
const getDistanceFromPosition = (position, start) => {
  if (!position || !start) {
    return Infinity;
  }

  return isSameLocation(position, start) ? 0 : getDistanceKm(position, start);
};

/**
 * The transfer of `pool` leaving closest to `position`.
 *
 * Equal distances are the rule, not the edge case, once positions are matched
 * at city level: an airport pickup and the flight it feeds both "leave from
 * Hanoi". So a tie is broken on the journey's own shape before anything else —
 * a transfer some other one arrives at is not a head, the traveller reaches it
 * through that other leg first (KD-3639: walking the flight before its pickup
 * appended the pickup after Danang and dragged the day back to Hanoi).
 *
 * Only then the transfer's own id, never its rank in the store: removing a
 * transfer and picking it again puts it back at the end of the selection, and
 * that must not change the journey it describes. With no position at all —
 * the program declares no start location — every distance is Infinity and the
 * same two tie-breaks pick the head of the chain on their own.
 */
const findClosestTransfer = (pool, position) => {
  let closest;
  let closestDistance = Infinity;
  let closestIsFed;
  let closestKey;

  pool.forEach((transfer) => {
    const start = getTransferStart(transfer);
    const distance = getDistanceFromPosition(position, start);
    const isFed = pool.some(
      (other) =>
        other !== transfer && isSameLocation(getTransferEnd(other), start),
    );
    const key = String(transfer?.id ?? '');

    const isCloser =
      !closest ||
      distance < closestDistance ||
      (distance === closestDistance &&
        ((!isFed && closestIsFed) ||
          (isFed === closestIsFed && key < closestKey)));

    if (isCloser) {
      closest = transfer;
      closestDistance = distance;
      closestIsFed = isFed;
      closestKey = key;
    }
  });

  return closest;
};

/**
 * KD-3548 — the real final destination of a set of retained transfers.
 *
 * Several transfers can be retained on the same day, and the order they arrive
 * in carries no meaning: the store keeps them in selection order, not in travel
 * order — and removing one then putting it back moves it to the end of that
 * order without changing the journey one bit. So the travel order is rebuilt
 * from the locations themselves, exactly as a traveller would read it:
 *
 *   1. the journey opens on the transfer leaving closest to where the PROGRAM
 *      starts — that is where the traveller physically is when the day opens;
 *   2. the next leg is the transfer leaving closest to where the previous one
 *      arrived;
 *   3. and so on until every retained transfer has been walked.
 *
 * The end of the last leg is the day's real destination, and it is what the
 * program's own end location gives way to.
 *
 * There is no distance past which a leg is dropped: every transfer retained on
 * the day was sold to the traveller, so all of them are walked however far apart
 * they sit. A selection whose legs do not join up is a data problem to surface
 * through the conflict badge, not something to silently trim here.
 *
 * The only transfers left out are those with no readable start location: they
 * cannot be placed in the journey at all. When none of them can, the last one
 * retained answers — the caller has no better signal than the latest pick.
 */
const chainTransfersAndGetFinalDestination = (
  transfers = [],
  initialPosition,
) => {
  // A transfer with no destination cannot end the chain nor be walked through.
  const validTransfers = asArray(transfers).filter((transfer) =>
    Boolean(getTransferEnd(transfer)),
  );

  if (validTransfers.length === 0) {
    return undefined;
  }

  const remaining = validTransfers.filter((transfer) =>
    Boolean(getTransferStart(transfer)),
  );

  if (remaining.length === 0) {
    return getTransferEnd(validTransfers[validTransfers.length - 1]);
  }

  let current = findClosestTransfer(remaining, initialPosition);
  let lastWalked = current;

  while (current) {
    lastWalked = current;
    remaining.splice(remaining.indexOf(current), 1);

    current =
      remaining.length > 0
        ? findClosestTransfer(remaining, getTransferEnd(current))
        : undefined;
  }

  return getTransferEnd(lastWalked);
};

/**
 * The end location a day should be read at: the destination of the required
 * transfer retained on it, or the program's own end location.
 *
 * Falls back rather than returns nothing, on every degraded input — no program,
 * no extras, a transfer whose destination is missing. Returning `undefined`
 * would empty the hotel search filter, which is worse than the location being
 * merely nominal.
 *
 * The transfer's destination is returned even when it disagrees with where the
 * next program starts: that is where the traveller physically is, and the
 * mismatch is what the existing conflict badge is there to surface. Signalled,
 * never blocked.
 *
 * When several required transfers are retained on the same day, the chain they
 * form decides: the destination is the end of the last leg, not the end of the
 * last one selected.
 */
const getEffectiveEndPosition = ({
  program,
  dayExtras,
  currentExperience,
  nextExperience,
}) => {
  const fallback = program?.endPosition;

  const transfers = asArray(dayExtras).filter((extra) =>
    isRequiredTransfer({ extra, program, currentExperience, nextExperience }),
  );

  if (transfers.length === 0) {
    return fallback;
  }

  return (
    chainTransfersAndGetFinalDestination(transfers, program?.startPosition) ??
    fallback
  );
};

/**
 * The transition transfers retained for the edge between two experiences.
 *
 * A transfer connecting two programs is offered by whichever of the two
 * declares it, and stored under that one: the pair
 * (sourceExperienceId, transitionalExperienceId) is what names the edge, in
 * either direction. Matching on the pair rather than on a single side is what
 * keeps a transfer belonging to another edge of the same itinerary out.
 *
 * Where it is stored says nothing about which day it applies to. A transfer
 * declared among the experience's GLOBAL extras is filed on the FIRST day of
 * that experience, never on the last — so scanning the day being rendered, or
 * even the next one, finds nothing. The whole selection has to be searched.
 */
const getTransitionExtras = ({
  days,
  selectedExtras,
  experienceId,
  nextExperienceId,
  catalogEntries,
}) => {
  if (!experienceId || !nextExperienceId) {
    return [];
  }

  // dmc keeps the selection in a flat provider array, to in a per-day zustand
  // field. Both are read, so one helper serves the two architectures.
  const pool = [
    ...asArray(selectedExtras),
    ...asArray(days).flatMap((d) => asArray(d?.selectedExtras)),
  ];

  const linksTheTwo = (extra) => {
    const source = extra?.sourceExperienceId ?? extra?.experienceId;
    const target = extra?.transitionalExperienceId;

    if (target) {
      return (
        (source === experienceId && target === nextExperienceId) ||
        (source === nextExperienceId && target === experienceId)
      );
    }

    // Reloaded from a saved trip: the pair that named the edge is gone — the
    // TripDayOption record never carried it. Two ways back to it, and the day a
    // transfer is stored on is not one of them: a global extra is filed on the
    // FIRST day of its experience, while the only day that should move is the
    // last. Without this the itinerary silently reverts to the program's own
    // location the moment it is reopened or exported.

    // 1. Scope. A global extra belongs to the experience as a whole, not to the
    //    day it happens to be filed on, so it applies where that experience
    //    ends. This is what carries a recommended transfer, which is always
    //    saved global. Whether it is a transfer at all, and whether it is
    //    retained rather than merely offered, stays isRequiredTransfer's call.
    if (extra?.scope === ExtraScopeEnum.GLOBAL && source === experienceId) {
      return true;
    }

    // 2. The catalogue, which still names the edge through the recommendation —
    //    including for a day-scoped transfer, and in either direction.
    const catalogEntry = findCatalogEntry(extra?.id, catalogEntries);

    return (
      (source === experienceId &&
        recommendsExperience(catalogEntry, nextExperienceId)) ||
      (source === nextExperienceId &&
        recommendsExperience(catalogEntry, experienceId))
    );
  };

  // The same transfer can sit in both sources at once; keeping the duplicate
  // would feed the chain a leg that goes nowhere.
  const seen = new Set();

  return pool.filter((extra) => {
    if (!linksTheTwo(extra) || seen.has(extra.id)) {
      return false;
    }

    seen.add(extra.id);
    return true;
  });
};

/**
 * The effective end location of one day of an itinerary, resolved from the store.
 *
 * Wraps getEffectiveEndPosition with the context every caller was re-deriving by
 * hand: the transfer that carries the traveller to the next program is what ends
 * the last day of the current one, and it is neither stored on that day nor
 * necessarily declared by that experience.
 *
 * Applied on the LAST day of the experience only — the day the traveller
 * actually leaves. On any earlier day of a multi-day program the transfer has
 * not happened yet, so the program's own location stands.
 *
 * `program` is passed in rather than derived, because the two apps index the
 * programs of a day differently (`day.experienceDayIndex` in dmc,
 * `experience.dayIndex` in to).
 */
export const getDayEffectiveEndPosition = ({
  days,
  dayIndex,
  experiencesMap,
  experience,
  program,
  selectedExtras,
}) => {
  const day = days?.[dayIndex];
  const currentExperience = experience ?? experiencesMap?.[day?.experienceId];

  const currentProgram =
    program ??
    currentExperience?.programs?.[(day?.experienceDayIndex ?? 1) - 1];

  const nextDay = days?.[dayIndex + 1];
  const nextExperienceId = nextDay?.experienceId;
  const nextExperience = nextExperienceId
    ? experiencesMap?.[nextExperienceId]
    : undefined;

  const experienceId = currentExperience?.id ?? day?.experienceId;

  // The last day of the experience is the one the next day no longer belongs to.
  // Same reading as the rest of the composer, and it holds at the end of the
  // trip too, where there is no next day at all.
  const isLastDayOfExperience = nextExperienceId !== experienceId;

  const transitionExtras = isLastDayOfExperience
    ? getTransitionExtras({
        days,
        selectedExtras,
        experienceId,
        nextExperienceId,
        catalogEntries: getCatalogEntries({
          program: currentProgram,
          currentExperience,
          nextExperience,
        }),
      })
    : [];

  // A transition transfer is filed wherever its scope dictates — for a global
  // extra, the FIRST day of the experience. It belongs to the day the traveller
  // actually leaves, so it is taken out of every other day's own extras rather
  // than moving the day it merely happens to be stored on. A global extra is
  // reached through the transition path above instead, on the last day only.
  const ownExtras = asArray(day?.selectedExtras).filter(
    (extra) =>
      !extra?.transitionalExperienceId &&
      extra?.scope !== ExtraScopeEnum.GLOBAL,
  );

  // A transfer stored on the very day being rendered is reached twice — once as
  // one of its own extras, once as the transition of the edge. Chaining it twice
  // would add a leg that goes nowhere.
  const seen = new Set();
  const dayExtras = [...ownExtras, ...transitionExtras].filter((extra) => {
    if (seen.has(extra?.id)) {
      return false;
    }

    seen.add(extra?.id);
    return true;
  });

  return getEffectiveEndPosition({
    program: currentProgram,
    dayExtras,
    currentExperience,
    nextExperience,
  });
};

/**
 * KD-3548 — the position the hotel search of a day must default to.
 *
 * The expected behaviour is explicit: on the day the transfer is taken, the
 * default hotel location changes to the transfer's DESTINATION. The program's
 * own end location is precisely the value that must stop being used, so it is
 * never what this returns once a transfer has moved the day.
 *
 * The search filter only speaks in city ids, and a transfer arrives at a point
 * of interest as often as at a city — an airport carries no city relation at
 * all, so its destination cannot name one. The ticket answers that too: the
 * transfer's destination and the START location of the next program are the
 * same place ("Must the transfer end location always match the start location of
 * the following program? YES"). When the destination cannot name a city, the
 * next program's start position names the same one, and it is a configured
 * position, so it carries the relation.
 *
 * Falls back to the destination itself when neither resolves: the caller reads
 * the city id and leaves the filter alone when there is none, which is still
 * closer to the truth than filtering on the place the traveller has left.
 */
export const getDayHotelSearchPosition = ({
  days,
  dayIndex,
  experiencesMap,
  experience,
  program,
  selectedExtras,
}) => {
  const effectiveEndPosition = getDayEffectiveEndPosition({
    days,
    dayIndex,
    experiencesMap,
    experience,
    program,
    selectedExtras,
  });

  const day = days?.[dayIndex];
  const currentExperience = experience ?? experiencesMap?.[day?.experienceId];
  const currentProgram =
    program ??
    currentExperience?.programs?.[(day?.experienceDayIndex ?? 1) - 1];

  // No transfer moved this day: the program answers, exactly as before KD-3548.
  if (effectiveEndPosition === currentProgram?.endPosition) {
    return effectiveEndPosition;
  }

  if (getPositionCityId(effectiveEndPosition)) {
    return effectiveEndPosition;
  }

  const nextDay = days?.[dayIndex + 1];
  const nextExperience = nextDay?.experienceId
    ? experiencesMap?.[nextDay.experienceId]
    : undefined;
  const nextStartPosition =
    nextExperience?.programs?.[(nextDay?.experienceDayIndex ?? 1) - 1]
      ?.startPosition;

  return getPositionCityId(nextStartPosition)
    ? nextStartPosition
    : effectiveEndPosition;
};

/**
 * The city id of a position, for the hotel search filter.
 *
 * `cityId` is mapped from the `cityLocation` relation on the API, and the
 * experience detail does not populate that relation explicitly — it is read off
 * the foreign key, so it should be there, but nothing in the payload guarantees
 * it. Callers must treat a missing id as "no default location" and leave the
 * filter untouched rather than send an undefined one.
 */
export const getPositionCityId = (position) =>
  position?.cityId ?? position?.cityLocation?.id;
